import { Col, Form, Input, Row, Spin } from "antd"
import { getLocalStorageBrowserLocation } from "helpers/location"
import { ListingActionButtons } from "layoutsV2/pro/ProNewListingLayout/ListingActionButtons"
import { debounce } from "lodash"
import GooglePlacesNewInput from "modules/google/GooglePlacesNewInput"
import useGeocoder from "modules/google/hooks/useGeocoder"
import usePredictions from "modules/google/hooks/usePredictions"
import { serialize } from "object-to-formdata"
import { useCallback, useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { useParams } from "react-router-dom"
import { modelSelector } from "store/selectors"
import { tripLatLngSelector, updateTrip } from "store/trips"
import { CustomForm } from "ui/Form"
import { notification } from "ui/Notification"
import { LocationCard } from "./LocationCard"
import { LocationInfo } from "./LocationForm"
import TripLocation from "./utils/Location"
import style from "./ListingLocation.module.css"
import { setFormTouched, setFormValues } from "store/proNewListingForms"

export const ListingLocation = () => {
  const { TextArea } = Input

  const [form] = Form.useForm()
  const dispatch = useDispatch()
  const { id } = useParams()
  const { t } = useTranslation()
  const tripLatLng = useSelector(tripLatLngSelector)
  const geocoder = useGeocoder(null)
  const trip = useSelector(modelSelector("trip"))
  const browserLocation = useMemo(() => getLocalStorageBrowserLocation(), [])
  const [centerLatLng, setCenterLatLng] = useState(tripLatLng || browserLocation)
  const [meetingLatLng, setMeetingLatLng] = useState(null)
  const [manualAddress, setManualAddress] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isDirty, setIsDirty] = useState(false)
  const [selectPlace] = usePredictions()

  useEffect(() => {
    if (trip?.address) {
      const tripLatLng = { lat: trip.latitude, lng: trip.longitude }
      setCenterLatLng(tripLatLng)
      setMeetingLatLng(trip?.address)
    }
  }, [trip])

  useEffect(() => {
    if (!meetingLatLng) {
      form.setFieldsValue({ address: "" }) // Clear the address field if meetingLatLng is null
    }
  }, [meetingLatLng, form])

  useEffect(() => {
    if (meetingLatLng) {
      dispatch(setFormValues("location", { address: meetingLatLng }))
      dispatch(setFormTouched("location", isDirty))
    } else {
      dispatch(setFormValues("location", { address: "" }))
      dispatch(setFormTouched("location", isDirty))
    }
  }, [meetingLatLng, isDirty, dispatch])

  const changeHandler = ({ target: { name, value } }) => {
    form.setFieldsValue({ [name]: value }) // Update the form field value
  }

  const handleValuesChange = (changedValues, allValues) => {
    debugger
    dispatch(setFormValues("location", allValues))
    dispatch(setFormTouched("location", true))
  }

  // eslint-disable-next-line
  const debounceCoordinatesToAddress = useCallback(
    debounce(
      (location) =>
        geocoder({ location }, (place) => {
          changeHandler({ target: { name: "address", value: place.formatted_address } })
        }),
      200
    ),
    []
  )
  const placeChangedHandler = useCallback(({ target: { coordinates, value } }) => {
    setIsEditing(false)
    if (coordinates) debounceCoordinatesToAddress(coordinates)
    setCenterLatLng(coordinates)
    if (value) {
      setMeetingLatLng(value)
      setIsDirty(true)
    }
  }, []) // eslint-disable-line

  const getAddressComponent = useCallback((addressComponents, types) => {
    const component = addressComponents.find((comp) => types.some((t) => comp.types.includes(t)))
    return component ? { long_name: component.long_name, short_name: component.short_name } : null
  }, [])

  const parseAddressComponents = useCallback(
    (place) => {
      const addressComponents = place.address_components
      const getComponent = (types) => getAddressComponent(addressComponents, types)

      const streetNumber = getComponent(["street_number"])
      const route = getComponent(["route"])
      const premise = getComponent(["premise"])
      const subpremise = getComponent(["subpremise"])

      let streetAddress = ""
      if (streetNumber || route) {
        streetAddress = [streetNumber?.short_name, route?.short_name].filter(Boolean).join(" ")
      }
      if (!streetAddress && place.formatted_address) {
        streetAddress = place.formatted_address.split(",")[0]?.trim() || ""
      }
      if (subpremise?.short_name) {
        streetAddress = [subpremise.short_name, streetAddress].filter(Boolean).join(", ")
      } else if (premise?.short_name && !streetAddress.includes(premise.short_name)) {
        streetAddress = [premise.short_name, streetAddress].filter(Boolean).join(", ")
      }

      const city =
        getComponent(["locality"]) ||
        getComponent(["postal_town"]) ||
        getComponent(["sublocality"]) ||
        getComponent(["administrative_area_level_2"]) ||
        getComponent(["administrative_area_level_3"])

      const state = getComponent(["administrative_area_level_1"])
      const country = getComponent(["country"])
      const zip = getComponent(["postal_code"])

      return {
        street_address: streetAddress,
        city: city?.long_name || "",
        state: state?.short_name || state?.long_name || "",
        country: country?.long_name || "",
        zip_code: zip?.long_name || ""
      }
    },
    [getAddressComponent]
  )

  const updateManualAddressFields = useCallback(
    (place) => {
      if (manualAddress && place.address_components) {
        const parsedAddress = parseAddressComponents(place)
        form.setFieldsValue(parsedAddress)
      }
    },
    [manualAddress, form, parseAddressComponents]
  )

  const handleGeocoderResult = useCallback(
    (place) => {
      if (place?.formatted_address) {
        setMeetingLatLng(place.formatted_address)
        form.setFieldsValue({ address: place.formatted_address })
        updateManualAddressFields(place)
      }
    },
    [form, setMeetingLatLng, updateManualAddressFields]
  )

  const handleMarkerDragEnd = useCallback(
    (newCoordinates) => {
      setCenterLatLng(newCoordinates)
      setIsDirty(true)
      geocoder({ location: newCoordinates }, handleGeocoderResult)
    },
    [geocoder, handleGeocoderResult]
  )

  const handleEdit = () => {
    setIsEditing(true) // Enable "Edit" mode
  }

  const handleFormValidation = (callback) => {
    form
      .validateFields()
      .then(processFormSubmission(callback))
      .catch((error) => {
        console.error("Validation failed:", error)
      })
  }

  const processFormSubmission = (callback) => async () => {
    const formValues = form.getFieldsValue()
    const { additional_directions } = formValues

    if (!meetingLatLng) {
      notification.error({ message: "Please confirm the address first." })
      return
    }

    let resolvedCoordinates = centerLatLng
    if (!resolvedCoordinates?.lat || !resolvedCoordinates?.lng) {
      console.warn("Location save missing coordinates", { meetingLatLng, centerLatLng, formValues })

      const geocoded = await new Promise((resolve) => {
        try {
          geocoder({ address: meetingLatLng }, (place) => {
            const location = place?.geometry?.location
            if (location) {
              resolve({
                lat: location.lat(),
                lng: location.lng(),
                formatted: place?.formatted_address
              })
            } else {
              resolve(null)
            }
          })
        } catch (error) {
          console.error(error)
          resolve(null)
        }
      })

      if (!geocoded?.lat || !geocoded?.lng) {
        notification.error({ message: "Unable to determine location coordinates. Please confirm the address again." })
        return
      }

      resolvedCoordinates = { lat: geocoded.lat, lng: geocoded.lng }
      setCenterLatLng(resolvedCoordinates)
      if (geocoded.formatted) {
        setMeetingLatLng(geocoded.formatted)
      }
    }

    const formData = serialize(
      {
        trip: {
          additional_directions: additional_directions || "",
          latitude: resolvedCoordinates?.lat,
          longitude: resolvedCoordinates?.lng,
          address: meetingLatLng
        }
      },
      { allowEmptyArrays: true }
    )
    setIsLoading(true)
    dispatch(updateTrip(id, formData))
      .then(() => {
        dispatch(setFormTouched("location", false))
        setIsDirty(false)
        callback?.()
        setIsLoading(false)
      })
      .catch(() => {
        setIsLoading(false)
      })
  }

  const onFinish = (callback) => {
    handleFormValidation(callback)
  }

  return (
    <Spin spinning={isLoading} size="large" style={{ width: "100%" }}>
      <CustomForm initialValues={{ ...trip }} form={form} layout="vertical" onFinish={onFinish} onValuesChange={handleValuesChange}>
        <Row gutter={[16, 16]} className="mb-3xl">
          <Col xs={24} sm={24} md={12}>
            {(meetingLatLng && !isEditing) ||
              (!manualAddress && (
                <Form.Item name="address">
                  <GooglePlacesNewInput
                    placeholder="Enter your address"
                    bsSize="sm" // Changed to 'lg' for better visibility
                    onChange={placeChangedHandler}
                    dropDownMenuClassName="mt-2 shadow-sm" // Added shadow for dropdown menu
                    resetIfChanged
                    meetingLatLng={meetingLatLng}
                    manualAddress={manualAddress}
                    setManualAddress={setManualAddress}
                    setCenterLatLng={setCenterLatLng}
                    setMeetingLatLng={(val) => {
                      setMeetingLatLng(val)
                      setIsDirty(true)
                    }}
                  />
                </Form.Item>
              ))}
            {meetingLatLng && !isEditing && !manualAddress && (
              <LocationCard name={"location"} meetingLatLng={meetingLatLng} onEdit={handleEdit} t={t} />
            )}

            {manualAddress && (
              <LocationInfo
                form={form}
                t={t}
                setManualAddress={setManualAddress}
                setCenterLatLng={setCenterLatLng}
                setMeetingLatLng={(val) => {
                  setMeetingLatLng(val)
                  setIsDirty(true)
                }}
                selectPlace={selectPlace}
                showMapVerificationMessage={true}
              />
            )}

            {((meetingLatLng && !isEditing) || manualAddress) && (
              <div className={`mt-20 ${style.addresField}`}>
                <Form.Item name="additional_directions" label={t("listings.tabs.location.special_instructions")}>
                  <TextArea placeholder={t("listings.tabs.location.special_instructions_placeholder")} rows={4} />
                </Form.Item>
              </div>
            )}
          </Col>
          <Col xs={24} sm={24} md={12}>
            <TripLocation height={450} centerLatLng={centerLatLng} meetingLatLng={meetingLatLng} onMarkerDragEnd={handleMarkerDragEnd} />

            {((meetingLatLng && !isEditing) || manualAddress) && (
              <div className={`mt-20 ${style.addresFieldMobile}`}>
                <Form.Item name="additional_directions" label={t("listings.tabs.location.special_instructions")}>
                  <TextArea placeholder={t("listings.tabs.location.special_instructions_placeholder")} rows={4} />
                </Form.Item>
              </div>
            )}
          </Col>
        </Row>
        <ListingActionButtons form={form} onFinish={onFinish} />
      </CustomForm>
    </Spin>
  )
}
